/**
 * This file is intended to be a mostly drop in replacement for react-use-audio-player.
 *
 * react-use-audio-player relies on howler.js. Howler.js is mainly used to make the web audio api easier to use.
 * We can't really use web audio because we need pitch preservation.
 *
 * Every time you call player.load() in react-use-audio-player, it would destory and recreate the holwer instance.
 * This lead to a lot of time to switch between audio chunks.
 *
 * This player keeps a pool of html5 audio elements (default 3) and prewarms the next audio chunk
 * before it is needed.
 */

import { isSSR } from '@/env';
import type {
  AudioPoolError,
  AudioPoolLoadOptions,
  AudioPoolPrewarmOptions,
  AudioPoolSnapshot,
} from '@listening/frontend/audio-chunk-player/pool-player';
import {
  AudioPoolPlayerContext,
  type AudioPoolPlayerHandle,
  AudioPoolPlayerMethodsContext,
  type AudioPoolPlayerMethods,
} from '@listening/frontend/audio-chunk-player/pool-player-context';
import type { LogSettings } from '@listening/frontend/logging/app-logger';
import React, {
  type ComponentProps,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { rootLogger } from '../root-logger';

const moduleLogger = rootLogger.child({ module: 'player' });

export { useAudioPoolPlayerContext } from '@listening/frontend/audio-chunk-player/pool-player-context';

/**
 * We will only ever create maxPoolSize HTMLAudioElement instances.
 * But a PoolItem represents a single audio chunk.
 *
 * Whenever prewarm or load is called with a src, we first check to see if a pool item exists with
 * that same src, if it does we return it.
 *
 * If it doesn't we evict the least recently used item and create a new one (reusing the HTMLAudioElement).
 */
export type AudioPlayerPoolItem = {
  el: HTMLAudioElement;
  // LRU key
  src: string;
  lastUsed: number;
  // timing https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/audio#events
  timing: {
    loadCalledAt?: number;
    canPlayCalledAt?: number;
    canPlayThroughCalledAt?: number;
    loadStartedCalledAt?: number;
    loadedMetadataCalledAt?: number;
    loadedDataCalledAt?: number;
  };
  perLoadListeners: Array<{ type: string; listener: EventListener }>;
};

type LoadCallbacks = {
  onCanPlay?: (item: AudioPlayerPoolItem) => void;
  onError?: (item: AudioPlayerPoolItem, error: MediaError | null) => void;
  onPlay?: (item: AudioPlayerPoolItem) => void;
  onEnded?: (item: AudioPlayerPoolItem) => void;
};

type TracingCallbacks = {
  onCanPlayThrough?: (item: AudioPlayerPoolItem) => void;
  onLoadStarted?: (item: AudioPlayerPoolItem) => void;
  onLoadedMetadata?: (item: AudioPlayerPoolItem) => void;
  onLoadedData?: (item: AudioPlayerPoolItem) => void;
};

type GlobalCallbacks = {
  onAbort?: (el: HTMLAudioElement) => void;
  onEmptied?: (el: HTMLAudioElement) => void;
  onWaiting?: (el: HTMLAudioElement) => void;
  onStalled?: (el: HTMLAudioElement) => void;
  onPlaying?: (el: HTMLAudioElement) => void;
  onSeeking?: (el: HTMLAudioElement) => void;
  onSeeked?: (el: HTMLAudioElement) => void;
  onPause?: (el: HTMLAudioElement) => void;
};

type CallbacksFns = LoadCallbacks & TracingCallbacks & GlobalCallbacks;

type AudioControls = {
  volume: number;
  muted: boolean;
  rate: number;
};

type AudioPrewarmLoadOptions = {
  onCanPlay?: CallbacksFns['onCanPlay'];
  onError?: CallbacksFns['onError'];
};

type AudioLoadOptions = {
  [K in keyof AudioControls]?: AudioControls[K] | undefined;
} & AudioPrewarmLoadOptions & {
    onEnded?: CallbacksFns['onEnded'];
  } & (
    | {
        autoplay: true;
        onPlay?: CallbacksFns['onPlay'];
      }
    | {
        autoplay?: false | undefined;
        onPlay?: undefined;
      }
  );

type AudioPoolPlayerSnapshot = Readonly<AudioControls> & {
  readonly duration: number;
  readonly src: string;
  readonly error: MediaError | null;

  readonly poolSize: number;
  readonly poolCount: number;
  readonly poolItems: Map<string, AudioPlayerPoolItem>;
  readonly poolItem: AudioPlayerPoolItem | null;
  /** Monotonic seek counter; bumped so a paused within-chunk seek produces an
   * observable snapshot change (position itself is kept out of the snapshot). */
  readonly seekCount: number;
} & (
    | {
        readonly isPlaying: true;
        readonly isPaused: false;
      }
    | {
        readonly isPlaying: false;
        readonly isPaused: true;
      }
  ) &
  (
    | {
        isError: false;
        error: null;
      }
    | {
        isError: true;
        error: MediaError;
      }
  ) &
  (
    | {
        readonly isReady: true;
        readonly isLoading: false;
      }
    | {
        readonly isReady: false;
        readonly isLoading: true;
      }
    | {
        readonly isReady: false;
        readonly isLoading: false;
      }
  );

const defaultSnapshot: AudioPoolPlayerSnapshot = Object.freeze({
  volume: 1,
  muted: false,
  rate: 1,

  duration: 0,
  src: '',
  error: null,

  poolSize: 3,
  poolCount: 0,
  poolItem: null,
  poolItems: new Map(),
  seekCount: 0,

  isLoading: false,
  isReady: false,
  isError: false,
  isPlaying: false,
  isPaused: true,
});

type Subscriber = () => void;

type Controller = ReturnType<typeof createController>;

// Web-internals escape hatch (pool stress test): the raw controller's load
// callbacks carry the HTMLAudioElement pool item, which the shared
// `AudioPoolPlayerHandle` deliberately does not expose.
const WebAudioPoolControllerContext = React.createContext<Controller | null>(
  null,
);

export function useWebAudioPoolController(): Controller {
  const ctx = React.use(WebAudioPoolControllerContext);
  if (!ctx)
    throw new Error(
      'useWebAudioPoolController must be used within AudioPoolPlayerProvider (client)',
    );
  return ctx;
}

function snapshotEquals(
  a: AudioPoolPlayerSnapshot,
  b: AudioPoolPlayerSnapshot,
) {
  return (
    a.isLoading === b.isLoading &&
    a.isReady === b.isReady &&
    a.isPlaying === b.isPlaying &&
    a.isPaused === b.isPaused &&
    a.duration === b.duration &&
    a.src === b.src &&
    a.rate === b.rate &&
    a.volume === b.volume &&
    a.muted === b.muted &&
    a.poolSize === b.poolSize &&
    a.poolCount === b.poolCount &&
    a.isError === b.isError &&
    a.poolItems.size === b.poolItems.size &&
    a.poolItem === b.poolItem &&
    a.seekCount === b.seekCount
  );
}

function controlsEquals(a: AudioControls, b: AudioControls) {
  return a.volume === b.volume && a.muted === b.muted && a.rate === b.rate;
}

function createController(params: AudioPoolPlayerProviderOptions) {
  const { poolSize: maxPoolSize = defaultSnapshot.poolSize, logSettings } =
    params;
  if (maxPoolSize < 1) throw new Error('maxPoolSize must be at least 1');
  const moduleName = 'PoolAudioPlayer';
  const logger = logSettings
    ? logSettings.parentLogger.child(
        { module: moduleName },
        logSettings.logOptions,
      )
    : rootLogger.child({ module: moduleName });
  logger.debug('Initializing pool audio player controller');

  const pool = new Map<string, AudioPlayerPoolItem>();
  let currentItem: AudioPlayerPoolItem | null = null;
  let seekCount = 0;
  const subs = new Set<Subscriber>();

  let globalControls: AudioControls = {
    volume: defaultSnapshot.volume,
    muted: defaultSnapshot.muted,
    rate: defaultSnapshot.rate,
  };

  const computeSnapshot = () => {
    const el = currentItem?.el;
    const readyState = el?.readyState ?? 0;
    const duration = el && Number.isFinite(el.duration) ? el.duration : 0;

    const src = currentItem?.src ?? '';
    const loaded = !!src;
    const error = el?.error ?? null;
    const isError = error !== null;

    const isLoading = loaded && readyState < HTMLMediaElement.HAVE_CURRENT_DATA;
    const isReady = loaded && !isLoading;

    const isPlaying = Boolean(el && duration !== 0 && !el.paused && !isLoading);
    const isPaused = !isPlaying;

    const snapshot = {
      isLoading,
      isReady,
      isPlaying,
      isPaused,
      duration,
      src,
      rate: el?.playbackRate ?? globalControls.rate,
      volume: el?.volume ?? globalControls.volume,
      muted: el?.muted ?? globalControls.muted,
      poolSize: maxPoolSize,
      poolCount: pool.size,
      poolItems: pool,
      poolItem: currentItem,
      seekCount,
      error,
      isError,
    } as AudioPoolPlayerSnapshot;

    return snapshot;
  };
  let lastSnapshot = computeSnapshot();

  const notify = () => {
    const next = computeSnapshot();
    if (!snapshotEquals(next, lastSnapshot)) {
      lastSnapshot = next;
      for (const cb of subs) {
        cb();
      }
    }
  };
  const subscribe = (cb: Subscriber) => {
    subs.add(cb);
    return () => subs.delete(cb);
  };

  const getSnapshot = () => lastSnapshot;

  const pauseAllExcept = (keep?: HTMLAudioElement) => {
    for (const { el } of pool.values()) {
      if (keep && el === keep) continue;
      try {
        el.pause();
      } catch (error) {
        logger.warn({ error }, 'pauseAllExcept: pause error');
      }
    }
  };

  const assignConstantAttributes = (el: HTMLAudioElement) => {
    el.preload = 'auto';
    el.crossOrigin = el.crossOrigin ?? 'anonymous';
    el.preservesPitch = true;
    // oxlint-disable anti-slop/no-chained-type-assertions -- vendor-prefixed preservesPitch not in standard HTMLAudioElement type
    try {
      (
        el as unknown as { webkitPreservesPitch?: boolean }
      ).webkitPreservesPitch = true;
      (el as unknown as { mozPreservesPitch?: boolean }).mozPreservesPitch =
        true;
    } catch (error) {
      logger.warn({ error }, 'Failed to set vendor flags for audio element');
    }
  };

  const _loadSrc = (
    loadParams: { src: string } & (
      | { options: AudioLoadOptions; isPrewarm?: false }
      | { options: AudioPrewarmLoadOptions; isPrewarm: true }
    ),
  ) => {
    const controls: AudioControls = loadParams.isPrewarm
      ? globalControls
      : {
          muted: loadParams.options.muted ?? globalControls.muted,
          volume: loadParams.options.volume ?? globalControls.volume,
          rate: loadParams.options.rate ?? globalControls.rate,
        };
    if (!controlsEquals(controls, globalControls)) globalControls = controls;

    const item = (function upsertFromPool() {
      const src = loadParams.src;
      const now = performance.now();
      const existing = pool.get(src);

      if (existing) {
        logger.debug('loadSrc: reusing existing item from pool');
        existing.lastUsed = now;
        return existing;
      }

      let elToUse: HTMLAudioElement;
      if (pool.size < maxPoolSize) {
        logger.debug('loadSrc: creating new element');
        elToUse = new Audio();
        elToUse.addEventListener('abort', () => {
          logger.debug('globalCallback: abort');
          params.globalCallbacks?.onAbort?.(elToUse);
          notify();
        });
        elToUse.addEventListener('emptied', () => {
          logger.debug('globalCallback: emptied');
          params.globalCallbacks?.onEmptied?.(elToUse);
          notify();
        });
        elToUse.addEventListener('waiting', () => {
          logger.debug('globalCallback: waiting');
          params.globalCallbacks?.onWaiting?.(elToUse);
          notify();
        });
        elToUse.addEventListener('stalled', () => {
          logger.debug('globalCallback: stalled');
          params.globalCallbacks?.onStalled?.(elToUse);
          notify();
        });
        elToUse.addEventListener('playing', () => {
          logger.debug('globalCallback: playing');
          params.globalCallbacks?.onPlaying?.(elToUse);
          notify();
        });
        elToUse.addEventListener('seeking', () => {
          logger.debug('globalCallback: seeking');
          params.globalCallbacks?.onSeeking?.(elToUse);
          notify();
        });
        elToUse.addEventListener('seeked', () => {
          logger.debug('globalCallback: seeked');
          params.globalCallbacks?.onSeeked?.(elToUse);
          notify();
        });
        elToUse.addEventListener('pause', () => {
          logger.debug(
            {
              duration: elToUse.duration,
              currentTime: elToUse.currentTime,
              paused: elToUse.paused,
            },
            'globalCallback: pause',
          );
          params.globalCallbacks?.onPause?.(elToUse);
          // paused is called before ended, which makes us show a pause button instead of a loading circle when switching chunks
          // If we can detect that this paused event is an ended event, we can defer the notify().
          if (elToUse.currentTime === elToUse.duration)
            setTimeout(() => {
              notify();
            }, 20);
          else notify();
        });
      } else {
        let lruVal: AudioPlayerPoolItem | null = null;
        for (const v of pool.values()) {
          if (currentItem && v.el === currentItem.el) continue;
          if (!lruVal || v.lastUsed < lruVal.lastUsed) {
            lruVal = v;
          }
        }
        const poolItemToEvict =
          maxPoolSize === 1 && currentItem ? currentItem : lruVal;
        if (poolItemToEvict) {
          logger.debug('loadSrc: evicting item from pool');
          poolItemToEvict.el.pause();
          for (const l of poolItemToEvict.perLoadListeners) {
            poolItemToEvict.el.removeEventListener(l.type, l.listener);
          }
          poolItemToEvict.perLoadListeners.length = 0;
          poolItemToEvict.el.removeAttribute('src');
          poolItemToEvict.el.src = '';
          poolItemToEvict.el.load(); // This resets the readyState to HAVE_NOTHING
          elToUse = poolItemToEvict.el;
          pool.delete(poolItemToEvict.src);
        } else {
          throw new Error('No item to evict');
        }
      }
      const item: AudioPlayerPoolItem = {
        el: elToUse,
        src,
        lastUsed: now,
        timing: {},
        perLoadListeners: [],
      };
      pool.set(src, item);
      return item;
    })();

    if (!loadParams.isPrewarm) currentItem = item;

    assignConstantAttributes(item.el);
    item.el.volume = controls.volume;
    item.el.muted = controls.muted;
    item.el.playbackRate = controls.rate;
    item.el.currentTime = 0;

    const isAlreadyLoaded =
      item.el.src === loadParams.src &&
      item.el.readyState >= HTMLMediaElement.HAVE_METADATA &&
      !item.el.error;

    if (isAlreadyLoaded) {
      logger.debug(
        { readyState: item.el.readyState },
        'loadSrc: already loaded',
      );
      loadParams.options.onCanPlay?.(item);
      if (item.el.error) loadParams.options.onError?.(item, item.el.error);
    } else {
      logger.debug(
        {
          src: loadParams.src,
          elSrc: item.el.src,
          elReadyState: item.el.readyState,
          elError: item.el.error,
        },
        `loadSrc: ${loadParams.isPrewarm ? 'prewarm' : 'load'} new src`,
      );
      for (const l of item.perLoadListeners) {
        item.el.removeEventListener(l.type, l.listener);
      }
      item.perLoadListeners = [];
      item.timing = {};
      item.el.src = loadParams.src;

      const onCanPlayOnce: EventListener = () => {
        params.globalCallbacks?.onCanPlay?.(item);
        loadParams.options.onCanPlay?.(item);
        item.timing.canPlayCalledAt = performance.now();
        notify();
      };
      item.el.addEventListener('canplay', onCanPlayOnce, { once: true });
      item.perLoadListeners.push({
        type: 'canplay',
        listener: onCanPlayOnce,
      });

      const onCanPlayAny: EventListener = () => {
        notify();
      };
      item.el.addEventListener('canplay', onCanPlayAny);
      item.perLoadListeners.push({ type: 'canplay', listener: onCanPlayAny });

      const onError: EventListener = () => {
        logger.warn({ error: item.el.error }, 'loadSrc: error');
        params.globalCallbacks?.onError?.(item, item.el.error);
        loadParams.options.onError?.(item, item.el.error);
        notify();
      };
      item.el.addEventListener('error', onError, { once: true });
      item.perLoadListeners.push({ type: 'error', listener: onError });

      const onCpt: EventListener = () => {
        logger.debug('loadSrc: canplaythrough');
        item.timing.canPlayThroughCalledAt = performance.now();
        params.globalCallbacks?.onCanPlayThrough?.(item);
        notify();
      };
      item.el.addEventListener('canplaythrough', onCpt, { once: true });
      item.perLoadListeners.push({ type: 'canplaythrough', listener: onCpt });

      const onLoadStart: EventListener = () => {
        logger.debug('loadSrc: loadstart');
        item.timing.loadStartedCalledAt = performance.now();
        params.globalCallbacks?.onLoadStarted?.(item);
        notify();
      };
      item.el.addEventListener('loadstart', onLoadStart, { once: true });
      item.perLoadListeners.push({
        type: 'loadstart',
        listener: onLoadStart,
      });

      const onLoadedMetadata: EventListener = () => {
        logger.debug('loadSrc: loadedmetadata');
        item.timing.loadedMetadataCalledAt = performance.now();
        params.globalCallbacks?.onLoadedMetadata?.(item);
        notify();
      };
      item.el.addEventListener('loadedmetadata', onLoadedMetadata, {
        once: true,
      });
      item.perLoadListeners.push({
        type: 'loadedmetadata',
        listener: onLoadedMetadata,
      });

      const onLoadedData: EventListener = () => {
        logger.debug('loadSrc: loadeddata');
        item.timing.loadedDataCalledAt = performance.now();
        params.globalCallbacks?.onLoadedData?.(item);
        notify();
      };
      item.el.addEventListener('loadeddata', onLoadedData, { once: true });
      item.perLoadListeners.push({
        type: 'loadeddata',
        listener: onLoadedData,
      });

      item.timing.loadCalledAt = performance.now();
      item.el.load();
      item.el.volume = controls.volume;
      item.el.muted = controls.muted;
      item.el.playbackRate = controls.rate;
    }

    return item;
  };

  const load = (src: string, options?: AudioLoadOptions) => {
    try {
      const item = _loadSrc({ src, isPrewarm: false, options: options ?? {} });
      const loadOptions = options ?? {};
      notify();
      if (item.perLoadListeners.length > 0) {
        const keep: typeof item.perLoadListeners = [];
        for (const l of item.perLoadListeners) {
          if (l.type === 'play' || l.type === 'ended') {
            try {
              item.el.removeEventListener(l.type, l.listener);
            } catch (error) {
              logger.warn({ error }, 'load: removeEventListener failed');
            }
          } else {
            keep.push(l);
          }
        }
        item.perLoadListeners = keep;
      }
      const onPlay: EventListener = () => {
        logger.debug('loadParamCallbacks.onPlay');
        params.globalCallbacks?.onPlay?.(item);
        loadOptions.onPlay?.(item);
        notify();
      };
      item.el.addEventListener('play', onPlay, { once: true });
      item.perLoadListeners.push({ type: 'play', listener: onPlay });

      const onEnded: EventListener = () => {
        logger.debug('loadParamCallbacks.onEnded');
        params.globalCallbacks?.onEnded?.(item);
        loadOptions.onEnded?.(item);
        notify();
      };
      item.el.addEventListener('ended', onEnded, { once: true });
      item.perLoadListeners.push({ type: 'ended', listener: onEnded });

      if (options?.autoplay) {
        logger.debug('load: autoplay');
        void play().catch((error: unknown) => {
          logger.warn({ error }, 'load: play error');
        });
      }
    } catch (error: unknown) {
      logger.warn({ error }, 'load: load error');
      throw error;
    } finally {
      notify();
    }
  };

  const prewarmLoad = (src: string, options?: AudioPrewarmLoadOptions) => {
    // A prefetch that lands after its chunk became current would rewind it
    // (`_loadSrc` resets `currentTime`).
    if (maxPoolSize === 1 || currentItem?.src === src) {
      return;
    }
    try {
      _loadSrc({ src, isPrewarm: true, options: options ?? {} });
      notify();
    } catch (error: unknown) {
      logger.warn({ error }, 'prewarmLoad: load error');
      throw error;
    } finally {
      notify();
    }
  };

  const play = async () => {
    logger.debug('play');
    if (!currentItem) return;
    try {
      pauseAllExcept(currentItem.el);
      await currentItem.el.play();
    } catch (error) {
      // A pause() (or a new load) before play() resolves rejects the play
      // promise with a benign AbortError — expected when the user toggles
      // quickly, not a fault. Swallow it; rethrowing surfaced it as an unhandled
      // rejection (Sentry WEB-16). Rethrow anything else.
      // https://developer.chrome.com/blog/play-request-was-interrupted
      if (error instanceof DOMException && error.name === 'AbortError') {
        logger.debug({ error }, 'play: interrupted by pause (ignored)');
        return;
      }
      logger.warn({ error }, 'play: play error');
      throw error;
    } finally {
      notify();
    }
  };

  const pause = () => {
    if (!currentItem) return;
    currentItem.el.pause();
    notify();
  };

  const togglePlayPause = async () => {
    const snapshot = getSnapshot();
    if (snapshot.isPlaying) {
      pause();
    } else {
      await play();
    }
  };

  const stop = () => {
    if (!currentItem) return;
    try {
      currentItem.el.pause();
    } catch (error: unknown) {
      logger.warn({ error }, 'stop: stop error');
    } finally {
      notify();
    }
  };

  const seek = (seconds: number) => {
    try {
      logger.debug({
        seconds,
        currentItem: currentItem?.el.currentTime,
      });
      if (currentItem) currentItem.el.currentTime = seconds;
    } catch (error: unknown) {
      logger.warn({ error }, 'seek: seek error');
    } finally {
      // Publish the seek so paused consumers resample: position isn't in the
      // snapshot, and while paused the browser emits no state change we'd
      // otherwise propagate.
      seekCount += 1;
      notify();
    }
  };

  const getPositionSec = () => (currentItem ? currentItem.el.currentTime : 0);
  const getPositionMs = () => getPositionSec() * 1000;

  const setRate = (rate: number) => {
    globalControls.rate = rate;
    for (const { el } of pool.values()) {
      el.playbackRate = rate;
      assignConstantAttributes(el);
    }
    notify();
  };

  const setVolume = (vol: number) => {
    globalControls.volume = vol;
    for (const { el } of pool.values()) {
      el.volume = vol;
    }
    notify();
  };

  const mute = () => {
    globalControls.muted = true;
    for (const { el } of pool.values()) {
      el.muted = true;
    }
    notify();
  };

  const unmute = () => {
    globalControls.muted = false;
    for (const { el } of pool.values()) {
      el.muted = false;
    }
    notify();
  };

  const destroy = () => {
    pauseAllExcept();
    for (const { el, perLoadListeners } of pool.values()) {
      try {
        for (const l of perLoadListeners) {
          try {
            el.removeEventListener(l.type, l.listener);
          } catch (error) {
            logger.warn({ error }, 'destroy: removeEventListener failed');
          }
        }
        perLoadListeners.length = 0;
        el.pause();
        el.removeAttribute('src');
        el.src = '';
        el.load();
      } catch (error) {
        logger.warn({ error }, 'destroy: element cleanup failed');
      }
    }
    pool.clear();
    currentItem = null;
    subs.clear();
  };

  return {
    subscribe,
    getSnapshot,
    load,
    prewarmLoad,
    play,
    togglePlayPause,
    pause,
    stop,
    seek,
    getPositionSec,
    getPositionMs,
    setRate,
    setVolume,
    mute,
    unmute,
    destroy,
  };
}

const UNKNOWN_MEDIA_ERROR: AudioPoolError = {
  code: 'media-unknown',
  message: 'Unknown media error',
};

const toPoolError = (error: MediaError | null): AudioPoolError =>
  error
    ? { code: `media-${String(error.code)}`, message: error.message }
    : UNKNOWN_MEDIA_ERROR;

function toSharedSnapshot(s: AudioPoolPlayerSnapshot): AudioPoolSnapshot {
  const base = {
    volume: s.volume,
    muted: s.muted,
    rate: s.rate,
    duration: s.duration,
    src: s.src,
    poolSize: s.poolSize,
    poolCount: s.poolCount,
    seekCount: s.seekCount,
  };
  const playing = s.isPlaying
    ? { isPlaying: true as const, isPaused: false as const }
    : { isPlaying: false as const, isPaused: true as const };
  const error = s.isError
    ? { isError: true as const, error: toPoolError(s.error) }
    : { isError: false as const, error: null };
  const readiness = s.isReady
    ? { isReady: true as const, isLoading: false as const }
    : s.isLoading
      ? { isReady: false as const, isLoading: true as const }
      : { isReady: false as const, isLoading: false as const };
  return { ...base, ...playing, ...error, ...readiness };
}

function adaptPrewarmOptions(
  options?: AudioPoolPrewarmOptions,
): AudioPrewarmLoadOptions | undefined {
  if (!options) return undefined;
  const { onError } = options;
  return {
    onCanPlay: options.onCanPlay,
    onError: onError
      ? (_item, mediaError) => onError(toPoolError(mediaError))
      : undefined,
  };
}

function adaptLoadOptions(
  options?: AudioPoolLoadOptions,
): AudioLoadOptions | undefined {
  if (!options) return undefined;
  const { onError } = options;
  const common = {
    volume: options.volume,
    muted: options.muted,
    rate: options.rate,
    onCanPlay: options.onCanPlay,
    onEnded: options.onEnded,
    onError: onError
      ? (_item: AudioPlayerPoolItem, mediaError: MediaError | null) =>
          onError(toPoolError(mediaError))
      : undefined,
  };
  return options.autoplay === true
    ? { ...common, autoplay: true, onPlay: options.onPlay }
    : { ...common, autoplay: options.autoplay, onPlay: undefined };
}

function AudioPoolPlayerProviderClient({
  children,
  ...options
}: ComponentProps<typeof AudioPoolPlayerProvider>) {
  const [controller] = useState(() => createController(options));
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    () => defaultSnapshot,
  );

  // Methods-only, identity stable across snapshot ticks — so `getPositionMs`
  // consumers (the highlight manager) read the player without re-rendering
  // 20x/s on the snapshot.
  const methods: AudioPoolPlayerMethods = useMemo(
    () => ({
      load: (src, loadOptions) =>
        controller.load(src, adaptLoadOptions(loadOptions)),
      prewarmLoad: (src, prewarmOptions) =>
        controller.prewarmLoad(src, adaptPrewarmOptions(prewarmOptions)),
      play: controller.play,
      pause: controller.pause,
      togglePlayPause: controller.togglePlayPause,
      stop: controller.stop,
      seek: controller.seek,
      getPositionSec: controller.getPositionSec,
      getPositionMs: controller.getPositionMs,
      setRate: controller.setRate,
      setVolume: controller.setVolume,
      mute: controller.mute,
      unmute: controller.unmute,
    }),
    [controller],
  );

  const value: AudioPoolPlayerHandle = useMemo(
    () => ({ ...toSharedSnapshot(snapshot), ...methods }),
    [snapshot, methods],
  );

  useEffect(
    () => () => {
      try {
        controller.destroy();
      } catch (error) {
        moduleLogger.warn(
          { err: error },
          'AudioPoolPlayerProviderClient: destroy failed',
        );
      }
    },
    [controller],
  );

  return (
    <WebAudioPoolControllerContext value={controller}>
      <AudioPoolPlayerMethodsContext value={methods}>
        <AudioPoolPlayerContext value={value}>
          {children}
        </AudioPoolPlayerContext>
      </AudioPoolPlayerMethodsContext>
    </WebAudioPoolControllerContext>
  );
}

const ssrAudioPlayer: AudioPoolPlayerHandle = {
  ...toSharedSnapshot(defaultSnapshot),
  load: () => {
    throw new Error('load is not supported in SSR');
  },
  prewarmLoad: () => {
    throw new Error('prewarmLoad is not supported in SSR');
  },
  play: () => {
    throw new Error('play is not supported in SSR');
  },
  pause: () => {
    throw new Error('pause is not supported in SSR');
  },
  togglePlayPause: () => {
    throw new Error('togglePlayPause is not supported in SSR');
  },
  stop: () => {
    throw new Error('stop is not supported in SSR');
  },
  seek: () => {
    throw new Error('seek is not supported in SSR');
  },
  getPositionSec: () => 0,
  getPositionMs: () => 0,
  setRate: () => {
    throw new Error('setRate is not supported in SSR');
  },
  setVolume: () => {
    throw new Error('setVolume is not supported in SSR');
  },
  mute: () => {
    throw new Error('mute is not supported in SSR');
  },
  unmute: () => {
    throw new Error('unmute is not supported in SSR');
  },
};

type AudioPoolPlayerProviderOptions = {
  logSettings?: LogSettings;
  poolSize?: number;
  trackTiming?: boolean;
  globalCallbacks?: CallbacksFns;
};

export function AudioPoolPlayerProvider({
  children,
  ...options
}: AudioPoolPlayerProviderOptions & {
  children: React.ReactNode;
}) {
  if (isSSR) {
    return (
      <AudioPoolPlayerMethodsContext value={ssrAudioPlayer}>
        <AudioPoolPlayerContext value={ssrAudioPlayer}>
          {children}
        </AudioPoolPlayerContext>
      </AudioPoolPlayerMethodsContext>
    );
  }
  return (
    <AudioPoolPlayerProviderClient {...options}>
      {children}
    </AudioPoolPlayerProviderClient>
  );
}
